import { currentSessionSignal, sessionEndedSince, sessionFetch } from '@papan/api-client'
import { parseDbOperationEnded, type DbOperationEnded } from '@papan/types'

/** First polls are quick (most background Replaces land in seconds), then they slow down. */
const FAST_POLL_MS = 3_000
const FAST_POLLS = 40
const SLOW_POLL_MS = 15_000
/** A Replace can wait behind other runs; past this, the share view stops watching (the record keeps its outcome). */
export const SHARE_REPLACE_WATCH_MS = 60 * 60_000

export interface WatchShareReplaceOptions {
  apiBase: string
  /** The session TOKEN that made the upload — the record is keyed to it (a later file switch mints another). */
  sessionId: string
  recordId: string
  /** False once the view is gone (or the watch was cancelled): the loop stops at its next step. */
  isActive: () => boolean
  fetchImpl?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  now?: () => number
}

/**
 * #1090 (review, 2026-10-19) — watch a background Replace this share view started (its upload answered 202 queued)
 * until it ends: the outcome as a `DbOperationEnded` (the DB editor's notice copy), or `null` — not this session's
 * record (404), the view gone, the session it started in ended (#1316 — a later poll would ask as the next session), or
 * the watch window passed. Transient failures poll again.
 */
export async function watchShareReplace(opts: WatchShareReplaceOptions): Promise<DbOperationEnded | null> {
  const doFetch = opts.fetchImpl ?? sessionFetch   // #1316: each poll bound to the session it starts in
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const now = opts.now ?? Date.now
  const deadline = now() + SHARE_REPLACE_WATCH_MS
  const url = `${opts.apiBase}/api/share-links/session/${opts.sessionId}/engine-db-replace/${encodeURIComponent(opts.recordId)}`
  const start = currentSessionSignal()
  for (let poll = 0; opts.isActive() && now() < deadline; poll++) {
    await sleep(poll < FAST_POLLS ? FAST_POLL_MS : SLOW_POLL_MS)
    if (!opts.isActive() || sessionEndedSince(start)) return null
    let body: { state?: string; reason?: string | null } | null = null
    try {
      const res = await doFetch(url, { credentials: 'include' })
      // A refusal that will not change (404 not this token's record, 409 the link's settings changed, 410 it expired,
      // 401/403): stop — only a server error, a 429 or a network failure is asked again.
      if (res.status >= 400 && res.status < 500 && res.status !== 429) return null
      if (res.ok) body = await res.json() as { state?: string; reason?: string | null }
    } catch (err) {
      if (sessionEndedSince(start, err)) return null   // #1316: dropped with its session — never asked again
      /* transient — the next poll asks again */
    }
    if (!body || body.state === 'running' || typeof body.state !== 'string') continue
    return parseDbOperationEnded({
      fileId: opts.recordId, operation: 'replace', outcome: body.state,
      ...(body.reason ? { reason: body.reason } : {}),
    }) ?? parseDbOperationEnded({ fileId: opts.recordId, operation: 'replace', outcome: body.state })
  }
  return null
}
